import type { Header, Row, RowData } from '@tanstack/table-core';
import type { ColumnSizingState } from '@tanstack/vue-table';
import type { MaybeRefOrGetter } from 'vue';
import { computed, nextTick, onScopeDispose, shallowRef, toValue, watch } from 'vue';
import { AUTO_COLUMN_MAX_WIDTH, clampAutoColumnWidth, resolveColumnWidth } from './data-table-column-width';

/**
 * Layout phase for the relax-then-freeze column engine. While any auto column is
 * still being sized by CSS the table is `relaxed`. Once widths are committed to
 * px, it switches to `frozen`.
 */
export type DataTableColumnLayoutPhase = 'relaxed' | 'frozen';

export interface DataTableColumnFreezeWindowOptions<T extends RowData> {
	/** Whether the freeze engine is active. When false, all state is cleared. */
	enabled?: MaybeRefOrGetter<boolean>;
	/** Visible header objects in render order. */
	visibleColumns: MaybeRefOrGetter<Header<T, unknown>[]>;
	/** Current row model — used to know when the table has data to size against. */
	rows: MaybeRefOrGetter<Row<T>[]>;
	/** Manual column sizing state. Manual widths always win and are never relaxed. */
	columnSizing: MaybeRefOrGetter<ColumnSizingState | undefined>;
	/**
	 * Stable dependency signatures that, when changed, re-open relaxed mode for
	 * all auto columns.
	 *
	 * Pass refs/computeds that resolve to primitive signatures or other stable
	 * identities. Nested object mutation is intentionally not deep-watched here;
	 * callers should derive an explicit signature (for example
	 * `stabilizeColumnSizesDeps`) when content changes must reopen sizing.
	 */
	dependencies?: MaybeRefOrGetter<unknown>[];
	/** Predicate to identify columns that should be auto-sized. */
	isColumnAutoSized?: (columnId: string) => boolean;
	/** Header `<tr>` element used to read live `<th>` widths during the freeze window. */
	headerElement: MaybeRefOrGetter<HTMLElement | null | undefined>;
	/** True while the user drags a resize handle. Cancels relaxed mode early. */
	isResizingColumn?: MaybeRefOrGetter<boolean>;
	/** Quiet-period freeze delay (no header width changes for this long). */
	quietPeriodMs?: number;
	/** Hard cap after which widths are frozen even if they are still changing. */
	hardTimeoutMs?: number;
	/**
	 * Called when an explicit auto-fit (`autoFitColumn`) finishes measuring a
	 * column's full, uncapped content width. The caller persists this as a manual
	 * width so the fit is not re-capped on the next automatic pass.
	 */
	onColumnAutoFit?: (columnId: string, width: number) => void;
}

const DEFAULT_QUIET_PERIOD_MS = 120;
const DEFAULT_HARD_TIMEOUT_MS = 1000;

/**
 * Owns the relax-then-freeze column width engine.
 *
 * Auto columns start in a relaxed phase that lets CSS size grid tracks from
 * visible content. When header widths stop changing for `quietPeriodMs` (or the
 * `hardTimeoutMs` hard deadline expires), the current widths are snapshot to
 * `frozenColumnWidths` and tracks switch to explicit px values.
 */
export function useDataTableColumnFreezeWindow<T extends RowData>(options: DataTableColumnFreezeWindowOptions<T>) {
	const enabled = computed(() => toValue(options.enabled ?? true));
	const visibleColumns = computed(() => toValue(options.visibleColumns));
	const rows = computed(() => toValue(options.rows));
	const manualColumnSizing = computed(() => toValue(options.columnSizing) ?? {});
	const dependencySignatures = computed(() => options.dependencies?.map((dep) => toValue(dep)) ?? null);
	const headerElement = computed(() => toValue(options.headerElement));
	const isResizingColumn = computed(() => !!toValue(options.isResizingColumn));
	const quietPeriodMs = options.quietPeriodMs ?? DEFAULT_QUIET_PERIOD_MS;
	const hardTimeoutMs = options.hardTimeoutMs ?? DEFAULT_HARD_TIMEOUT_MS;

	const frozenColumnWidths = shallowRef<Record<string, number>>({});
	const relaxedColumnIds = shallowRef<Set<string>>(new Set());
	// Subset of relaxed columns currently being explicitly auto-fit. These size to
	// uncapped `max-content` and commit their measured width as a manual override
	// via `onColumnAutoFit` instead of the (capped) frozen-width map.
	const autoFitColumnIds = shallowRef<Set<string>>(new Set());

	const layoutPhase = computed<DataTableColumnLayoutPhase>(() => relaxedColumnIds.value.size > 0 ? 'relaxed' : 'frozen');

	// Track header widths between observer ticks so we can detect quiet periods.
	const observedColumnWidths = new Map<string, number>();
	let quietTimer: ReturnType<typeof setTimeout> | undefined;
	let hardTimer: ReturnType<typeof setTimeout> | undefined;
	let resizeObserver: ResizeObserver | null = null;

	const visibleAutoColumnIds = computed(() => visibleColumns.value
		.map((h) => h.id)
		.filter((id) => isAutoColumn(id)));

	// Keyed on column membership only — deliberately order- and pinning-agnostic.
	// Pinning, unpinning, and reordering re-position columns but never change their
	// content widths, and every auto column already holds a frozen width, so they
	// must not re-open the relaxed phase: doing so briefly blows out columns whose
	// content cannot break (e.g. a long no-whitespace value) before re-freezing.
	const columnMembershipKey = computed(() => visibleColumns.value
		.map((h) => h.id)
		.toSorted()
		.join('|'));

	// Re-open relaxed mode whenever the set of visible columns changes.
	watch(columnMembershipKey, () => {
		reopenRelaxedForAllAuto();
	}, { immediate: true });

	// Re-open when the row window itself changes — pagination/filter/sort usually
	// replace at least one edge row or the row count. This is intentionally an
	// escape hatch, not a full content hash: in-place middle-row edits are
	// ignored so scrolling/re-renders do not constantly reopen sizing. Callers
	// that need content edits to widen columns should pass an explicit signature
	// through `dependencies`.
	const rowsSignature = computed(() => {
		const list = rows.value;
		if (list.length === 0) return '';
		return `${list.length}|${list[0]?.id ?? ''}|${list.at(-1)?.id ?? ''}`;
	});

	watch(rowsSignature, () => {
		reopenRelaxedForAllAuto();
	});

	// Caller-provided dependencies (filters, search, etc.).
	if (options.dependencies) {
		watch(dependencySignatures, () => {
			reopenRelaxedForAllAuto();
		});
	}

	watch(enabled, (isEnabled) => {
		if (!isEnabled) {
			clearTimers();
			relaxedColumnIds.value = new Set();
			autoFitColumnIds.value = new Set();
			frozenColumnWidths.value = {};
			return;
		}

		reopenRelaxedForAllAuto();
	});

	// A real manual resize cancels the relaxed phase: snapshot whatever width the
	// CSS-sized track currently has, then let the resize drag continue from a
	// stable px baseline.
	watch(isResizingColumn, (resizing) => {
		if (resizing && relaxedColumnIds.value.size > 0) {
			commitFreeze();
		}
	});

	let disposed = false;

	onScopeDispose(() => {
		disposed = true;
		clearTimers();
	});

	// `observedColumnWidths` is intentionally a non-reactive Map: this computed
	// is only meaningful while the layout is frozen (cssTableCols short-circuits
	// to `max-content` for relaxed columns and never reads `resolvedColumnWidths`
	// for them). The observed entry is therefore a last-resort fallback for
	// columns that have no manual or frozen width yet — reactivity on every
	// ResizeObserver tick would re-render the whole table for no visible effect.
	const resolvedColumnWidths = computed<Record<string, number>>(() => {
		const widths: Record<string, number> = {};

		for (const header of visibleColumns.value) {
			const minSize = header.column.columnDef.minSize ?? 0;
			const manualWidth = manualColumnSizing.value[header.id];
			const autoWidth = frozenColumnWidths.value[header.id]
				?? observedColumnWidths.get(header.id)
				?? header.column.getSize();

			widths[header.id] = resolveColumnWidth({
				manualWidth,
				autoWidth,
				minSize,
				maxWidth: AUTO_COLUMN_MAX_WIDTH,
			});
		}

		return widths;
	});

	return {
		layoutPhase,
		frozenColumnWidths,
		relaxedColumnIds,
		resolvedColumnWidths,
		getResolvedColumnWidth,
		isColumnRelaxed,
		isColumnAutoFitting,
		autoFitColumn,
		resetColumnToRelaxed,
		freezeNow: commitFreeze,
		growFrozenColumnWidths,
	};

	/**
	 * Grow-only merge into the frozen widths map. Ignores columns already
	 * wider than the proposed value. Keeps external callers from mutating
	 * the shallowRef directly.
	 */
	function growFrozenColumnWidths(updates: Record<string, number>) {
		let changed = false;
		const next = { ...frozenColumnWidths.value };

		for (const [columnId, width] of Object.entries(updates)) {
			const cappedWidth = Math.min(AUTO_COLUMN_MAX_WIDTH, width);
			const current = next[columnId] ?? 0;

			if (cappedWidth > current) {
				next[columnId] = cappedWidth;
				changed = true;
			}
		}

		if (changed) {
			frozenColumnWidths.value = next;
		}
	}

	/**
	 * Returns true when the column does not have a manual width override and
	 * should be sized by the relax-then-freeze engine.
	 */
	function isAutoColumn(columnId: string) {
		if (options.isColumnAutoSized) {
			return options.isColumnAutoSized(columnId);
		}

		return !(manualColumnSizing.value && columnId in manualColumnSizing.value);
	}

	function isColumnRelaxed(columnId: string) {
		return relaxedColumnIds.value.has(columnId);
	}

	function isColumnAutoFitting(columnId: string) {
		return autoFitColumnIds.value.has(columnId);
	}

	/**
	 * Drops a single column's frozen width and re-opens its relaxed phase so the
	 * normal freeze window re-measures it. With `autoFit`, the column sizes to an
	 * uncapped `max-content` track and commits its measured width as a manual
	 * override via `onColumnAutoFit`; otherwise it re-freezes to the clamped
	 * automatic width.
	 */
	function reopenColumnRelaxed(columnId: string, { autoFit }: { autoFit: boolean }) {
		if (!enabled.value) {
			return;
		}

		const nextFrozen = { ...frozenColumnWidths.value };
		delete nextFrozen[columnId];
		frozenColumnWidths.value = nextFrozen;

		const nextRelaxed = new Set(relaxedColumnIds.value);
		nextRelaxed.add(columnId);
		relaxedColumnIds.value = nextRelaxed;

		// Keep the auto-fit flag authoritative per call: a capped re-measure
		// (autoFit: false) must clear any stale flag so a column mid-auto-fit is
		// not committed as an uncapped manual width.
		if (autoFit !== autoFitColumnIds.value.has(columnId)) {
			const nextAutoFit = new Set(autoFitColumnIds.value);
			if (autoFit) nextAutoFit.add(columnId);
			else nextAutoFit.delete(columnId);
			autoFitColumnIds.value = nextAutoFit;
		}

		observedColumnWidths.delete(columnId);

		startFreezeWindow();
	}

	/**
	 * Explicit "fit to content" for a single column (resize-handle double click).
	 * The column sizes to its full, uncapped width and commits as a manual width,
	 * so an intentional fit can exceed the automatic ceiling. Callers must clear
	 * any existing manual width first so the column renders in its relaxed (auto)
	 * track while measuring.
	 */
	function autoFitColumn(columnId: string) {
		reopenColumnRelaxed(columnId, { autoFit: true });
	}

	/**
	 * Re-measures a single column back to its clamped automatic width (used by the
	 * header "Reset width" action after its manual width has been cleared).
	 */
	function resetColumnToRelaxed(columnId: string) {
		reopenColumnRelaxed(columnId, { autoFit: false });
	}

	function getResolvedColumnWidth(columnId: string) {
		return resolvedColumnWidths.value[columnId] ?? 0;
	}

	/**
	 * Re-opens relaxed mode for every visible auto column. Called on initial
	 * mount, data/view changes, dependency changes, and when the column set
	 * itself shifts.
	 */
	function reopenRelaxedForAllAuto() {
		if (!enabled.value) {
			return;
		}

		const autoIds = visibleAutoColumnIds.value;

		if (autoIds.length === 0 || rows.value.length === 0) {
			// Nothing to size — clear any stale relaxed state.
			clearTimers();
			relaxedColumnIds.value = new Set();
			autoFitColumnIds.value = new Set();
			return;
		}

		const nextFrozen = { ...frozenColumnWidths.value };

		for (const id of autoIds) {
			delete nextFrozen[id];
		}

		frozenColumnWidths.value = nextFrozen;
		relaxedColumnIds.value = new Set(autoIds);
		autoFitColumnIds.value = new Set();
		observedColumnWidths.clear();

		startFreezeWindow();
	}

	/**
	 * Begins (or restarts) the freeze window: starts the hard deadline and the
	 * initial quiet timer once the relaxed CSS pass has rendered.
	 */
	function startFreezeWindow() {
		clearTimers();

		void nextTick(() => {
			startObservingHeader();
			scheduleQuietFreeze();

			hardTimer = setTimeout(() => {
				commitFreeze();
			}, hardTimeoutMs);
		});
	}

	function scheduleQuietFreeze() {
		if (quietTimer != null) {
			clearTimeout(quietTimer);
		}

		quietTimer = setTimeout(() => {
			commitFreeze();
		}, quietPeriodMs);
	}

	function startObservingHeader() {
		const root = headerElement.value;

		if (disposed || !root || typeof ResizeObserver === 'undefined') {
			return;
		}

		resizeObserver?.disconnect();

		resizeObserver = new ResizeObserver((entries) => {
			let changed = false;

			for (const entry of entries) {
				const el = entry.target as HTMLElement;
				const columnId = el.dataset.columnId;

				if (!columnId || !relaxedColumnIds.value.has(columnId)) {
					continue;
				}

				const width = entry.contentRect.width;
				const prev = observedColumnWidths.get(columnId);

				if (prev !== width) {
					observedColumnWidths.set(columnId, width);
					changed = true;
				}
			}

			if (changed) {
				scheduleQuietFreeze();
			}
		});

		for (const th of root.querySelectorAll<HTMLElement>('th[data-column-id]')) {
			const columnId = th.dataset.columnId;

			if (!columnId || !relaxedColumnIds.value.has(columnId)) {
				continue;
			}

			resizeObserver.observe(th);
			observedColumnWidths.set(columnId, th.getBoundingClientRect().width);
		}
	}

	/**
	 * Snapshots current `<th>` widths and switches relaxed columns to frozen.
	 */
	function commitFreeze() {
		if (relaxedColumnIds.value.size === 0) {
			clearTimers();
			return;
		}

		const nextFrozen = { ...frozenColumnWidths.value };
		const root = headerElement.value;

		// Build a columnId → <th> map in one pass. Avoids `CSS.escape` (not
		// available during jsdom teardown when this timer fires post-test) and
		// sidesteps any special-char escaping concerns for column ids.
		const thByColumnId = new Map<string, HTMLElement>();

		if (root) {
			for (const th of root.querySelectorAll<HTMLElement>('th[data-column-id]')) {
				const id = th.dataset.columnId;
				if (id) thByColumnId.set(id, th);
			}
		}

		// Iterate every relaxed column directly, not just the `<th>` currently
		// mounted. A column can be relaxed without a DOM node (e.g. while the
		// header is detached between renders); for those we fall back to the
		// last ResizeObserver snapshot instead of silently skipping them.
		//
		// Use the raw sub-pixel `getBoundingClientRect().width` — `Math.ceil`
		// here visibly grew each column by 0.2-0.9px when we flipped from the
		// CSS `max-content` track to the explicit `${frozen}px`, which the
		// flex column then absorbed as a 2-3px jump on every tab switch.
		// Auto-fit results are collected and emitted after the relaxed/auto-fit
		// state is cleared, so the column renders as a manual width once the
		// callback writes it back.
		const autoFitResults: Array<{ columnId: string; width: number }> = [];

		for (const columnId of relaxedColumnIds.value) {
			const minSize = visibleColumns.value.find((h) => h.id === columnId)?.column.columnDef.minSize ?? 0;
			const th = thByColumnId.get(columnId);
			const measured = th ? th.getBoundingClientRect().width : 0;
			const width = measured > 0 ? measured : (observedColumnWidths.get(columnId) ?? 0);

			if (width <= 0) {
				continue;
			}

			// Explicit auto-fit keeps the full content width (floor only, no ceiling);
			// automatic sizing clamps to the auto ceiling.
			if (autoFitColumnIds.value.has(columnId)) {
				autoFitResults.push({ columnId, width: Math.max(minSize, width) });
				continue;
			}

			nextFrozen[columnId] = clampAutoColumnWidth(width, minSize, AUTO_COLUMN_MAX_WIDTH);
		}

		frozenColumnWidths.value = nextFrozen;
		relaxedColumnIds.value = new Set();
		autoFitColumnIds.value = new Set();
		clearTimers();

		for (const { columnId, width } of autoFitResults) {
			options.onColumnAutoFit?.(columnId, width);
		}
	}

	function clearTimers() {
		if (quietTimer != null) {
			clearTimeout(quietTimer);
			quietTimer = undefined;
		}

		if (hardTimer != null) {
			clearTimeout(hardTimer);
			hardTimer = undefined;
		}

		resizeObserver?.disconnect();
		resizeObserver = null;
	}
}
